import { useState, useEffect } from "react";
import useMountEffect from "core/hooks/useMountEffect";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/pro-regular-svg-icons";

import Typography from "core/components/Typography";

const root = document.querySelector(":root");

function hslToHex(hslString) {
  let sep = hslString.indexOf(",") > -1 ? "," : " ";
  let hsl = hslString.substr(4).split(")")[0].split(sep);

  let h = hsl[0];
  let s = hsl[1].split("%")[0] / 100;
  let l = hsl[2].split("%")[0] / 100;

  let c = (1 - Math.abs(2 * l - 1)) * s,
    x = c * (1 - Math.abs(((h / 60) % 2) - 1)),
    m = l - c / 2,
    r = 0,
    g = 0,
    b = 0;

  if (0 <= h && h < 60) {
    r = c;
    g = x;
    b = 0;
  } else if (60 <= h && h < 120) {
    r = x;
    g = c;
    b = 0;
  } else if (120 <= h && h < 180) {
    r = 0;
    g = c;
    b = x;
  } else if (180 <= h && h < 240) {
    r = 0;
    g = x;
    b = c;
  } else if (240 <= h && h < 300) {
    r = x;
    g = 0;
    b = c;
  } else if (300 <= h && h < 360) {
    r = c;
    g = 0;
    b = x;
  }
  // Having obtained RGB, convert channels to hex
  r = Math.round((r + m) * 255).toString(16);
  g = Math.round((g + m) * 255).toString(16);
  b = Math.round((b + m) * 255).toString(16);

  // Prepend 0s, if necessary
  if (r.length == 1) r = "0" + r;
  if (g.length == 1) g = "0" + g;
  if (b.length == 1) b = "0" + b;

  return "#" + r + g + b;
}

const getRootColors = () => {
  let rootColors = {};
  [
    "--color-primary",
    "--color-accent",
    "--color-secondary",
    "--color-primary-content",
    "--color-secondary-content",
    "--color-accent-content",
    "--color-base-content",
    "--color-base-100",
    "--color-base-200",
    "--color-base-300",
  ].forEach((variable) => {
    const value = window
      .getComputedStyle(document.body)
      .getPropertyValue(variable);

    rootColors[variable] = value ? hslToHex(value) : "#000000";
  });

  return rootColors;
};

const ThemePicker = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [colors, setColors] = useState(null);

  const handleColorChange = (event) => {
    const { name, value } = event.target;

    setColors((previous) => {
      return {
        ...previous,
        [name]: value,
      };
    });
  };

  useEffect(() => {
    if (!colors) {
      setTimeout(() => {
        setColors(getRootColors());
      }, 3000);
    } else {
      Object.keys(colors).forEach((cssVariable) => {
        root.style.setProperty(cssVariable, colors[cssVariable]);
      });
    }
  }, [colors]);

  useMountEffect(() => {
    window.openSesame = () => {
      setIsOpen(true);
    };
  });

  return (
    <div
      style={{
        display: isOpen ? "block" : "none",
        position: "fixed",
        zIndex: 10000,
        bottom: "30px",
        right: "30px",
        height: "fit-content",
        width: "fit-content",
        padding: "10px 20px",
        paddingBottom: "20px",
        border: "2px solid goldenrod",
        borderRadius: "5px",
        backgroundColor: "#fff",
      }}
    >
      <FontAwesomeIcon
        icon={faClose}
        onClick={() => setIsOpen(false)}
        style={{ position: "absolute", right: "20px", top: "10px" }}
      />
      <Typography variant="h5">Colors</Typography>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          flexDirection: "column",
          gap: "12px",
          alignItems: "flex-start",
          justifyContent: "flex-start",
        }}
      >
        {colors &&
          Object.keys(colors).map((cssVariable) => {
            const prettyName = cssVariable
              .split("--color-")
              .filter((v) => v)[0]
              .split("-")
              .map(
                (name) =>
                  `${name.substring(0, 1).toUpperCase()}${name.substring(1)}`
              )
              .join(" ");

            return (
              <div
                key={cssVariable}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <input
                  type="color"
                  id={cssVariable}
                  name={cssVariable}
                  value={colors[cssVariable]}
                  onChange={handleColorChange}
                />
                <label htmlFor={cssVariable}>{prettyName}</label>
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default ThemePicker;
